import gql from "graphql-tag.macro";
import axios from "axios";
import { createHeadingWorkflowInstance } from "../../../../apps/workflow/workflowWidget/workflowWidget.api";
import {
  getV3ProjectList,
  saveProject,
  updateV3Project,
} from "../../../../apps/projectManagement/projectList/projectList.api";
import {
  loadCurrentUser,
  rolloverUsersToProject,
} from "../../../../apps/projectManagement/addProjectModal/addProjectModal.api";

const ALLOWED_HOSTS = new Set([
  "demo.masterdocs.com.au",
  "demo1.masterdocs.com.au",
  "demo2.masterdocs.com.au",
  "demo3.masterdocs.com.au",
  "test1.masterdocs.com.au",
  "test2.masterdocs.com.au",
  "test3.masterdocs.com.au",
  "dev1.dart.masterdocs.com.au",
  "dev2.dart.masterdocs.com.au",
  "dev3.dart.masterdocs.com.au",
  "testcorporate.dart.masterdocs.com.au",
  "testnoncorporate.dart.masterdocs.com.au",
  "testcompany.dart.masterdocs.com.au",
  "uat1.dart.masterdocs.com.au",
  "uat2.dart.masterdocs.com.au",
  "uat3.dart.masterdocs.com.au",
  "127.0.0.1:5003",
  "localhost:5003",
  "preview.dart.masterdocs.com.au",
  "proposals.masterdocs.com.au",
]);

const mutationReset = gql`
  mutation {
    reset {
      reset
      status
      message
    }
  }
`;

const mutationDeleteProjects = gql`
  mutation deleteProjects($legacyIds: [Int!]!) {
    deletedProjects(legacyIds: $legacyIds)
  }
`;

const mutationCleanupInstance = gql`
  mutation cleanupInstance {
    cleanupInstance
  }
`;

const mutationResetProject = gql`
  mutation {
    resetProject
  }
`;

const queryGetActiveWorkflowModelIdByLegacyID = gql`
  query getActiveWorkflowModelIdByLegacyID($legacyID: Int!) {
    getActiveWorkflowModelIdByLegacyID(legacyID: $legacyID)
  }
`;

export const backupData = async () => {
  if (!ALLOWED_HOSTS.has(window.location.host)) {
    throw new Error(
      "This action cannot be performed in the production environment"
    );
  }
  const response = await axios.get(`${window.location.origin}/api/backup`, {
    params: {
      sessionID: window.app.Duchess.getCookie("SESSION_ID"),
      v3ProjectId: window.app.Duchess.currentProject._id,
    },
  });

  return response;
};

/**
 * I have to get all the projects in v3 that have heading workflow set up.
 * Iterates the above and get the legacy project id then communicate with v2 backend to get the list of documents
 * for each document, get all heading ids and string concat the heading-workflow-instance-id and headingID (i.e <heading-workflow-id>:<heading_id>)
 * Once I have the complete list of all heading-ids in a project, then I will call v3 BE to generate heading workflow instances for all the heading-ids
 */
export const recreateHeadingWorkflowInstance = async ({
  v2ProjectID,
  client,
}) => {
  if (window?.APPVERSION?.directory) {
    try {
      const {
        data: {
          getActiveWorkflowModelIdByLegacyID:
            v3ProjectIdAndHeadingWorkflowModelID,
        },
      } = await client.query({
        query: queryGetActiveWorkflowModelIdByLegacyID,
        variables: { legacyID: v2ProjectID },
        fetchPolicy: "no-cache",
        context: {
          headers: {
            "x-scope-instance": window.app.Duchess.currentInstance._id || "",
            "x-scope-project": window.app.Duchess.currentProject._id || "",
          },
        },
      });
      if (!v3ProjectIdAndHeadingWorkflowModelID) {
        throw new Error(
          `No active heading workflow model found in: ${v2ProjectID}`
        );
      }
      const [v3ProjectID, headingWorkflowModelID] =
        v3ProjectIdAndHeadingWorkflowModelID.split(":");
      const docs = await new Promise((reso, rej) => {
        window.app.Duchess.listObject(
          "Document",
          {
            filterBy: "usedInProject",
            filter: v2ProjectID,
          },
          (res) => {
            return res.success ? reso(res) : rej(res);
          }
        );
      });

      const { list: v2Doclist, success, message } = docs;

      if (success) {
        const IDs = [];
        const filteredDocuments =
          v2Doclist?.filter((d) => d?.archived !== 1) || [];

        console.log(
          `Recreating heading workflow instances for project: ${v2ProjectID}`
        );
        for (let i = 0; i <= filteredDocuments.length - 1; i++) {
          const filteredDoc = filteredDocuments[i];
          const v2Doc = await new Promise((resolve, reject) => {
            window.app.Duchess.readObject(
              "Document",
              {
                ID: filteredDoc?.id,
                populate: "conformsToStructure,conformsToStructure.headings",
              },
              (res) => {
                return res.success ? resolve(res) : reject(res);
              }
            );
          });

          const docHeadingIds =
            v2Doc?.object?.conformsToStructure?.headings.map(
              (o) => `${v3ProjectID}:${filteredDoc?.id}-${o.id}`
            );
          IDs.push(...docHeadingIds);
        }

        await createHeadingWorkflowInstance(
          {
            ids: IDs,
            modelId: headingWorkflowModelID,
            v3ProjectId: v3ProjectID,
          },
          { client }
        );
      }
    } catch (err) {
      console.error(err.message);
    }
  }
};

/**
 * Restores missing projects by creating them in the current instance and optionally rolling over users.
 *
 * @async
 * @param {Object} params - The parameters object.
 * @param {Array<Object>} params.projectsToRestore - Array of projects to restore. Each project should have `id`, `title`, and `organisation`.
 * @param {Object} params.client - The Apollo GraphQL client instance.
 * @returns {Promise<void>} Resolves when all projects have been processed.
 */
export async function restoreMissingProjects({
  projectsToRestore = [],
  client,
}) {
  if (!client || !Array.isArray(projectsToRestore)) return;

  const { firstname, lastname, email, _id } = await loadCurrentUser({ client });

  for (const { id, title = "", organisation } of projectsToRestore) {
    try {
      const {
        data: { createProjectInInstance },
      } = await client.mutate({
        mutation: gql`
          mutation CreateProjectInInstance(
            $instanceId: ObjectID
            $projectInput: CreateProjectInput
          ) {
            createProjectInInstance(
              parentId: $instanceId
              input: $projectInput
            ) {
              _id
            }
          }
        `,
        variables: {
          instanceId: window.app.Duchess.currentInstance._id,
          projectInput: {
            legacyId: id,
            name: title,
            disabled: false,
          },
        },
        context: {
          headers: {
            "x-scope-instance": window.app.Duchess.currentInstance._id || "",
            "x-scope-project": window.app.Duchess.currentProject._id || "",
          },
        },
      });

      if (email) {
        await rolloverUsersToProject(
          {
            toProject: createProjectInInstance._id,
            users: [
              {
                firstName: firstname,
                lastName: lastname,
                email,
                role: "Administrator",
              },
            ],
          },
          { client }
        );
      }
    } catch (err) {
      console.error("Failed to restore project", id, err);
    }
  }
}

export const restoreData = async ({ client }) => {
  if (!ALLOWED_HOSTS.has(window.location.host)) {
    throw new Error("Restore data cannot run in production");
  }

  try {
    // Perform the permission check first to prevent continuing and accidentally restoring the backup if the user lacks access.
    await client.mutate({
      mutation: mutationCleanupInstance,
      context: {
        headers: {
          "x-scope-instance": window.app.Duchess.currentInstance._id || "",
          "x-scope-project": window.app.Duchess.currentProject._id || "",
        },
      },
    });

    // Fetch backup and current projects
    const projectsResponse = await axios.get(
      `${window.location.origin}/api/getBackupProjects`, {
        params: {
          sessionID: window.app.Duchess.getCookie("SESSION_ID"),
          v3ProjectId: window.app.Duchess.currentProject._id,
        },
      }
    );
    // Restore database.
    // Recently added sessionID and v3ProjectId to params for the API call to check user access.
    const { data: restoreResponse } = await axios.get(
      `${window.location.origin}/api/restore`,
      {
        params: {
          sessionID: window.app.Duchess.getCookie("SESSION_ID"),
          v3ProjectId: window.app.Duchess.currentProject._id,
        },
      }
    );
    if (!restoreResponse?.success) {
      throw new Error(restoreResponse.message);
    }

    const currentProjects = projectsResponse?.data?.data?.currentProjects || [];
    const backupProjects = projectsResponse?.data?.data?.backupProjects || [];

    // Identify projects to be deleted
    const toBeDeletedProjects = currentProjects
      .filter(
        (project) =>
          !backupProjects.some(
            (backupProject) => backupProject.id === project.id
          )
      )
      .map(({ id }) => id);

    // Delete projects if necessary
    if (toBeDeletedProjects.length > 0) {
      await client.mutate({
        mutation: mutationDeleteProjects,
        variables: { legacyIds: toBeDeletedProjects },
        context: {
          headers: {
            "x-scope-instance": window.app.Duchess.currentInstance._id || "",
            "x-scope-project": window.app.Duchess.currentProject._id || "",
          },
        },
      });
    }

    // Recreate projects that are in backup but not in v3 database
    const v3Projects = await getV3ProjectList();

    const v2ProjectsNotInV3 =
      backupProjects?.filter(
        ({ id }) => !v3Projects.some(({ legacyId }) => legacyId == id)
      ) || [];

    await restoreMissingProjects({
      projectsToRestore: v2ProjectsNotInV3,
      client,
    });

    // Ensure V2 project data is synced to V3 and Keycloak.
    for (const { id, title, organisation } of backupProjects) {
      await updateV3Project({
        legacyId: id,
        name: title,
        _organisation: organisation,
      });
    }

    await Promise.all(
      backupProjects.map(({ id }) =>
        recreateHeadingWorkflowInstance({ v2ProjectID: id, client })
      )
    );

    window.app.Duchess.setCookie("CurrentProjectID", backupProjects[0].id);

    window.app.StateMachine.handleEvent("ViewMaintenanceEditor");
    setTimeout(() => {
      window.location.reload();
    }, 2000);
    return restoreResponse;
  } catch (error) {
    console.error("Restore data failed:", error);
    throw new Error(error);
  }
};

export const resetData = async ({ client }) => {
  if (!ALLOWED_HOSTS.has(window.location.host)) {
    throw new Error(
      "This action cannot be performed in the production environment"
    );
  }

  try {
    const { data } = await client.mutate({
      mutation: mutationResetProject,
      fetchPolicy: "no-cache",
      context: {
        headers: {
          "x-scope-instance": window.app.Duchess.currentInstance._id || "",
          "x-scope-project": window.app.Duchess.currentProject._id || "",
        },
      },
    });
    window.location.reload();
    return data;
  } catch (error) {
    throw new Error(error?.message);
  }
};

const progressTimeout = 60000;

export const updateAIEmbeddings = async ({
  clearBeforeResync,
  fields,
  client,
}) => {
  const fieldsParam = encodeURIComponent(btoa(JSON.stringify(fields || [])));
  const v3ProjectID = window.app.Duchess.currentProject._id;
  const v2ProjectID = window.app.Duchess.getCookie("CurrentProjectID");
  const clientIP = window.app.Duchess?.currentUser?.currentSession?.clientIP;

  const encodedClientIP = encodeURIComponent(clientIP);

  const response = await axios.get(
    `${
      window.location.origin
    }/api/resyncEmbeddings?v3ProjectId=${v3ProjectID}&v2ProjectId=${v2ProjectID}&clientIP=${encodedClientIP}&clearBeforeResync=${String(
      clearBeforeResync || false
    )}&fields=${fieldsParam}`
  );

  if (response.status !== 200) {
    return Promise.reject(
      new Error(
        `Failed to update embeddings because of error: ${response.statusText}`
      )
    );
  }

  const progressChannelName = `/duchess/vector-embedding-progress/${app.Duchess.getCookie(
    "CurrentUserUOID"
  )}`;
  let lastProgressInfo = null;

  Document.vectorEmbeddingProgressSocketId = app.Duchess.WebSocketSubscribe(
    progressChannelName,
    (progressInfo) => {
      lastProgressInfo = progressInfo;
    }
  );

  const readBufferTimeoutDuration = 60000;
  const pollIntervalDuration = 500;

  let donePacket = null;

  async function readProgressBuffer() {
    const readStartTime = Date.now();
    while (true) {
      if (lastProgressInfo) {
        if (lastProgressInfo.done) {
          donePacket = { success: true };
        }
        const progressInfo = lastProgressInfo;
        lastProgressInfo = null;
        return progressInfo;
      }
      await new Promise((resolve) => setTimeout(resolve, pollIntervalDuration));
      if (Date.now() - readStartTime > readBufferTimeoutDuration) {
        throw new Error("Embedding progress buffer read timed out");
      }
    }
  }

  function* progressGenerator() {
    while (true) {
      if (donePacket) {
        return donePacket;
      }
      yield readProgressBuffer();
    }
  }

  return { data: "started", progressIterator: progressGenerator() };
};
